import { FormEvent, useCallback, useRef, useState } from "react";
import { MapPin, Navigation, Search, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { MapView } from "@/components/Map";
import { trpc } from "@/lib/trpc";

type NearbyPlace = google.maps.places.PlaceResult & { distanceKm?: number };

const DEFAULT_CENTER = { lat: -37.8136, lng: 144.9631 };

function distanceKm(from: google.maps.LatLngLiteral, to?: google.maps.LatLngLiteral) {
  if (!to) return undefined;
  const earthRadius = 6371;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const dLat = ((to.lat - from.lat) * Math.PI) / 180;
  const dLng = ((to.lng - from.lng) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function NearbyTobaccoFinder() {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker[]>([]);
  const [query, setQuery] = useState("");
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [places, setPlaces] = useState<NearbyPlace[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [nearbyApproved, setNearbyApproved] = useState<Array<{
    id: number;
    name: string;
    address: string;
    suburb: string | null;
    postcode: string | null;
    latitude: string | null;
    longitude: string | null;
    phone: string | null;
    website: string | null;
  }>>([]);
  const approvedLocations = trpc.tobaccoLocations.listApproved.useQuery({ limit: 100 });

  const clearMarkers = useCallback(() => {
    markerRef.current.forEach((marker) => marker.setMap(null));
    markerRef.current = [];
  }, []);

  const searchNearby = useCallback((location: google.maps.LatLngLiteral) => {
    if (!mapRef.current || !window.google?.maps?.places) return;
    setIsSearching(true);
    setError("");
    setHasSearched(true);
    setCenter(location);
    mapRef.current.setCenter(location);
    mapRef.current.setZoom(14);
    clearMarkers();

    const approvedMatches = (approvedLocations.data ?? [])
      .filter((place) => place.latitude !== null && place.longitude !== null)
      .map((place) => ({ ...place, distanceKm: distanceKm(location, { lat: Number(place.latitude), lng: Number(place.longitude) }) }))
      .filter((place) => place.distanceKm !== undefined && place.distanceKm <= 10)
      .sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))
      .slice(0, 12);
    setNearbyApproved(approvedMatches.map(({ distanceKm: _distanceKm, ...place }) => place));

    const service = new google.maps.places.PlacesService(mapRef.current);
    service.nearbySearch(
      {
        location,
        radius: 5000,
        keyword: "tobacco shop vape shop convenience store",
      },
      (results, status) => {
        setIsSearching(false);
        const nextPlaces = status === google.maps.places.PlacesServiceStatus.OK && results
          ? results
              .filter((place) => place.name && place.geometry?.location)
              .slice(0, 12)
              .map((place) => {
                const position = place.geometry?.location?.toJSON();
                return { ...place, distanceKm: distanceKm(location, position) };
              })
          : [];
        setPlaces(nextPlaces);
        if (nextPlaces.length === 0 && approvedMatches.length === 0) {
          setError("No nearby public locations were found. Try a more specific city, postcode, or area.");
        } else {
          setError("");
        }
        const approvedMarkers = approvedMatches.map((place) => new google.maps.Marker({
          map: mapRef.current,
          position: { lat: Number(place.latitude), lng: Number(place.longitude) },
          title: place.name,
          label: "A",
        }));
        const mapMarkers = nextPlaces.map((place) => {
          const marker = new google.maps.Marker({
            map: mapRef.current,
            position: place.geometry?.location,
            title: place.name,
          });
          marker.addListener("click", () => {
            mapRef.current?.panTo(place.geometry!.location!);
          });
          return marker;
        });
        markerRef.current = [...approvedMarkers, ...mapMarkers];
      },
    );
  }, [approvedLocations.data, clearMarkers]);

  const geocodeAndSearch = (event: FormEvent) => {
    event.preventDefault();
    const address = query.trim();
    if (!address || !mapRef.current) return;
    setIsSearching(true);
    setError("");
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status !== "OK" || !results?.[0]?.geometry.location) {
        setIsSearching(false);
        setError("Unable to identify this location. Enter an Australian city, postcode, or area.");
        return;
      }
      searchNearby(results[0].geometry.location.toJSON());
    });
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Location access is not supported by this browser. Search by city or postcode instead.");
      return;
    }
    setIsSearching(true);
    setError("");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => searchNearby({ lat: coords.latitude, lng: coords.longitude }),
      () => {
        setIsSearching(false);
        setError("Unable to access your location. Check browser location permissions or search by city/postcode.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 },
    );
  };

  return (
    <section className="border-y border-slate-100 bg-white px-4 py-8 md:py-10" aria-labelledby="nearby-tobacco-title">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-blue-600">Find nearby support</p>
            <h2 id="nearby-tobacco-title" className="text-2xl font-bold text-slate-950 md:text-3xl">Find Nearby Tobacco and Authorized Retail Locations</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">Enter an Australian city, postcode, or area to view nearby places found on public maps. This site provides location discovery and information support only; it does not sell products, take orders, or process payments here.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-emerald-700"><ShieldCheck className="h-4 w-4" />Public location information. Please verify age requirements, stock availability, and authorization status before purchase.</div>
        </div>

        <div className="grid gap-5 lg:grid-cols-[1.35fr_0.65fr]">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-200 bg-white p-3 sm:flex-row">
              <form className="flex min-w-0 flex-1 gap-2" onSubmit={geocodeAndSearch}>
                <div className="relative min-w-0 flex-1"><MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Australian city, postcode, or area" className="pl-9" aria-label="Search city, postcode, or area" /></div>
                <Button type="submit" disabled={isSearching || !query.trim()} className="bg-blue-600 hover:bg-blue-700"><Search className="mr-2 h-4 w-4" />Search</Button>
              </form>
              <Button type="button" variant="outline" onClick={useCurrentLocation} disabled={isSearching}><Navigation className="mr-2 h-4 w-4" />Use my location</Button>
            </div>
            <MapView className="h-[360px] md:h-[420px]" initialCenter={DEFAULT_CENTER} initialZoom={11} onMapReady={(map) => { mapRef.current = map; }} />
          </div>

          <Card className="flex min-h-[360px] flex-col border-slate-200 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Nearby places</p><h3 className="mt-1 text-xl font-bold text-slate-950">Nearby public locations</h3></div><span className="rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700">{places.length + nearbyApproved.length} results</span></div>
            {isSearching && <p className="mt-5 animate-pulse text-sm text-slate-500">Searching nearby places…</p>}
            {!isSearching && error && <p className="mt-5 rounded-lg bg-amber-50 p-3 text-sm leading-6 text-amber-800">{error}</p>}
            {!isSearching && !error && !hasSearched && <p className="mt-5 text-sm leading-6 text-slate-500">Search to see nearby public locations here, with matching map markers.</p>}
            {!isSearching && !error && hasSearched && places.length === 0 && <p className="mt-5 text-sm leading-6 text-slate-500">No public locations to display. Try another location.</p>}
            <div className="mt-4 space-y-3 overflow-y-auto pr-1">
              {nearbyApproved.map((place) => {
                const position = { lat: Number(place.latitude), lng: Number(place.longitude) };
                const distance = distanceKm(center, position);
                return <button key={`approved-${place.id}`} type="button" className="w-full rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-left transition hover:border-emerald-300 hover:bg-emerald-50" onClick={() => mapRef.current?.panTo(position)}>
                  <div className="flex items-start justify-between gap-3"><span className="font-semibold text-slate-900">{place.name}</span>{distance !== undefined && <span className="shrink-0 text-xs text-slate-500">{distance.toFixed(1)} km</span>}</div>
                  <span className="mt-1 block text-xs leading-5 text-slate-600">{place.address}{place.suburb ? ` · ${place.suburb}` : ""}{place.postcode ? ` ${place.postcode}` : ""}</span>
                  <span className="mt-2 block text-xs font-semibold text-emerald-700">Approved public location · Please verify independently</span>
                </button>;
              })}
              {places.map((place) => (
                <button key={place.place_id || place.name} type="button" className="w-full rounded-xl border border-slate-100 bg-slate-50 p-3 text-left transition hover:border-blue-200 hover:bg-blue-50" onClick={() => place.geometry?.location && mapRef.current?.panTo(place.geometry.location)}>
                  <div className="flex items-start justify-between gap-3"><span className="font-semibold text-slate-900">{place.name}</span>{place.distanceKm !== undefined && <span className="shrink-0 text-xs text-slate-500">{place.distanceKm.toFixed(1)} km</span>}</div>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">{place.vicinity || place.formatted_address || "Public address unavailable from map service"}</span>
                  {place.opening_hours?.isOpen?.() !== undefined && <span className="mt-2 block text-xs font-medium text-emerald-700">{place.opening_hours.isOpen() ? "Map indicates open" : "Map indicates closed"}</span>}
                </button>
              ))}
            </div>
            <p className="mt-auto border-t border-slate-100 pt-4 text-xs leading-5 text-slate-500">Locations come from public map discovery results and do not represent IGET official authorization, inventory, or current operating status. Follow local laws and verify with the retailer before purchasing.</p>
          </Card>
        </div>
      </div>
    </section>
  );
}
